import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { cookieDomain } from "@utils/env";
import { isPublicPath } from "@utils/publicPaths";
import { PropsWithChildren } from "react";
import { useLocation, useNavigate } from "react-router";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { FronteraAuthProvider } from "@fronterahealth/frontera-auth0-react";
import { TooltipProvider } from "@fronterahealth/frontera-ui-components/ui/tooltip";

import "@components/notifications/notifications.css";
import { AdminDataProvider } from "@providers/AdminDataProvider";
import { AnalyticsProvider } from "@providers/AnalyticsProvider";
import { FeatureFlagProvider } from "@providers/FeatureFlagProvider";
import { InternationalizationProvider } from "@providers/InternationalizationProvider";
import { NotificationsProvider } from "@providers/NotificationsProvider";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

export function AppProviders({ children }: PropsWithChildren) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  if (isPublicPath(pathname)) {
    return (
      <QueryClientProvider client={queryClient}>
        <ToastContainer pauseOnFocusLoss={false} autoClose={2000} hideProgressBar />
        {children}
      </QueryClientProvider>
    );
  }

  return (
    <FronteraAuthProvider
      loginOnMount={true}
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      audience={import.meta.env.VITE_AUTH0_AUDIENCE}
      cookieDomain={cookieDomain}
      onRedirectCallback={(appState) => navigate(appState?.returnTo || window.location.pathname, { replace: true })}
    >
      <InternationalizationProvider>
        <QueryClientProvider client={queryClient}>
          <AdminDataProvider>
            <NotificationsProvider>
              <ToastContainer pauseOnFocusLoss={false} autoClose={2000} hideProgressBar />
              <FeatureFlagProvider>
                <AnalyticsProvider>
                  <TooltipProvider>{children}</TooltipProvider>
                </AnalyticsProvider>
              </FeatureFlagProvider>
            </NotificationsProvider>
          </AdminDataProvider>
        </QueryClientProvider>
      </InternationalizationProvider>
    </FronteraAuthProvider>
  );
}
